import { useParams, Link, useNavigate } from 'react-router-dom'
import { useRef, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { clientConversations } from '../../api/client'
import PageHeader from '../../components/PageHeader'

function formatTime(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  }
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
}

// Markdown components for AI messages
const markdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="ml-2">{children}</li>,
  h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
  code: ({ inline, children }) =>
    inline
      ? <code className="bg-gray-300 dark:bg-dial-elevated px-1 rounded text-sm font-mono">{children}</code>
      : <code className="block bg-gray-300 dark:bg-dial-elevated p-2 rounded text-sm font-mono overflow-x-auto mb-2">{children}</code>,
  pre: ({ children }) => <pre className="bg-gray-300 dark:bg-dial-elevated p-2 rounded overflow-x-auto mb-2">{children}</pre>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-400 dark:border-indices-muted pl-3 italic mb-2">{children}</blockquote>,
  a: ({ href, children }) => <a href={href} className="text-primary-600 dark:text-lume-teal underline" target="_blank" rel="noopener noreferrer">{children}</a>,
}

// User message markdown components
const userMarkdownComponents = {
  ...markdownComponents,
  code: ({ inline, children }) =>
    inline
      ? <code className="bg-primary-500 dark:bg-lume-teal/20 px-1 rounded text-sm font-mono">{children}</code>
      : <code className="block bg-primary-500 dark:bg-lume-teal/20 p-2 rounded text-sm font-mono overflow-x-auto mb-2">{children}</code>,
  pre: ({ children }) => <pre className="bg-primary-500 dark:bg-lume-teal/20 p-2 rounded overflow-x-auto mb-2">{children}</pre>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-primary-300 dark:border-lume-teal/40 pl-3 italic mb-2">{children}</blockquote>,
  a: ({ href, children }) => <a href={href} className="text-primary-200 dark:text-lume-green underline" target="_blank" rel="noopener noreferrer">{children}</a>,
}

// Trainer message markdown components (amber accent)
const trainerMarkdownComponents = {
  ...markdownComponents,
  code: ({ inline, children }) =>
    inline
      ? <code className="bg-amber-200/30 dark:bg-amber-500/20 px-1 rounded text-sm font-mono">{children}</code>
      : <code className="block bg-amber-200/30 dark:bg-amber-500/20 p-2 rounded text-sm font-mono overflow-x-auto mb-2">{children}</code>,
  pre: ({ children }) => <pre className="bg-amber-200/30 dark:bg-amber-500/20 p-2 rounded overflow-x-auto mb-2">{children}</pre>,
  a: ({ href, children }) => <a href={href} className="text-amber-700 dark:text-amber-300 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
}

function Message({ message, isUser, isTrainer, onCreateKB, onCorrect, onFlag }) {
  const [flagged, setFlagged] = useState(!!message.flaggedAt)
  const [reasoningOpen, setReasoningOpen] = useState(false)
  const rc = message.reasoningContext || null
  const hasThinking = rc && typeof rc.thinking === 'string' && rc.thinking.length > 0
  const hasTools = rc && Array.isArray(rc.tools_used) && rc.tools_used.length > 0
  const showReasoningToggle = !isUser && !isTrainer && (hasThinking || hasTools)
  if (isTrainer) {
    return (
      <div className="flex justify-start mb-3">
        <div className="max-w-[85%]">
          <div className="text-xs text-amber-400 mb-1 font-medium">
            {message.trainerName || 'Trainer'}
          </div>
          <div className="px-4 py-2 rounded-2xl rounded-bl-md bg-amber-500/10 border border-amber-500/30 text-amber-200">
            <div className="text-sm prose prose-sm max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={trainerMarkdownComponents}
              >
                {message.content}
              </ReactMarkdown>
            </div>
            <p className="text-xs text-amber-400/50 mt-1">
              {formatTime(message.createdAt)}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] px-4 py-2 rounded-2xl ${
          isUser
            ? 'bg-primary-600 dark:bg-lume-teal/20 text-white dark:text-lume-teal dark:border dark:border-lume-teal/40 rounded-br-md'
            : 'bg-gray-200 dark:bg-dial-surface text-gray-900 dark:text-indices-bright dark:border dark:border-dial-border rounded-bl-md'
        }`}
      >
        <div className="text-sm prose prose-sm max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={isUser ? userMarkdownComponents : markdownComponents}
          >
            {message.content}
          </ReactMarkdown>
        </div>
        <div className={`flex items-center justify-between mt-1 ${isUser ? 'text-primary-200 dark:text-lume-teal/50' : 'text-gray-500 dark:text-indices-muted'}`}>
          <p className="text-xs">
            {formatTime(message.createdAt)}
          </p>
          {!isUser && (
            <div className="flex items-center gap-2">
              {onCorrect && !message.correctedAt && (
                <button
                  onClick={() => onCorrect(message)}
                  className="text-xs hover:text-amber-500 dark:hover:text-amber-400 flex items-center gap-1 transition-colors"
                  title="Flag and correct this response"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                  </svg>
                </button>
              )}
              {onFlag && (
                <button
                  onClick={() => {
                    onFlag(message)
                    setFlagged(!flagged)
                  }}
                  className={`text-xs flex items-center gap-1 transition-colors ${
                    flagged
                      ? 'text-red-500 dark:text-red-400'
                      : 'hover:text-red-500 dark:hover:text-red-400'
                  }`}
                  title={flagged ? 'Remove flag' : 'Flag as low quality'}
                >
                  <svg className="w-3.5 h-3.5" fill={flagged ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                  </svg>
                </button>
              )}
              {onCreateKB && (
                <button
                  onClick={() => onCreateKB(message)}
                  className="text-xs hover:text-primary-600 dark:hover:text-lume-teal flex items-center gap-1 transition-colors"
                  title="Create KB article from this response"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span>KB</span>
                </button>
              )}
            </div>
          )}
        </div>
        {/* Corrected indicator */}
        {!isUser && message.correctedAt && (
          <div className="mt-2 px-2 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-center gap-1.5 text-xs text-amber-400 font-medium">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Corrected
            </div>
            {message.correctionNote && (
              <p className="text-xs text-amber-300/80 mt-1">{message.correctionNote}</p>
            )}
          </div>
        )}
        {!isUser && flagged && (
          <div className="mt-1.5 flex items-center gap-1 text-xs text-red-400">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
            </svg>
            Flagged{message.flagNote ? `: ${message.flagNote}` : ''}
          </div>
        )}
        {!isUser && message.reasoningContext?.kb_articles?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            <span className="text-[10px] text-indices-muted">KB:</span>
            {message.reasoningContext.kb_articles.map((article, i) => (
              <Link
                key={i}
                to={`/knowledge/${article.id}/edit`}
                className="text-[10px] px-1.5 py-0.5 bg-dial-elevated border border-dial-border rounded text-lume-teal/70 hover:text-lume-teal transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                {article.title}
              </Link>
            ))}
          </div>
        )}
        {showReasoningToggle && (
          <div className="mt-2">
            <button
              onClick={() => setReasoningOpen((v) => !v)}
              className="text-[10px] text-indices-muted hover:text-lume-teal transition-colors flex items-center gap-1 font-mono uppercase tracking-wider"
            >
              <svg className={`w-3 h-3 transition-transform ${reasoningOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {reasoningOpen ? 'Hide' : 'View'} reasoning
              {hasThinking && <span className="text-indices-muted/60">· {Math.ceil(rc.thinking.length / 4)} tokens</span>}
              {hasTools && <span className="text-indices-muted/60">· {rc.tools_used.length} tool{rc.tools_used.length === 1 ? '' : 's'}</span>}
            </button>
            {reasoningOpen && (
              <div className="mt-1.5 space-y-2">
                {hasThinking && (
                  <div className="p-2 bg-dial-black/50 border border-dial-border rounded">
                    <div className="text-[10px] text-indices-muted font-mono uppercase tracking-wider mb-1">Thinking</div>
                    <pre className="text-xs text-indices-medium whitespace-pre-wrap font-mono leading-relaxed max-h-64 overflow-y-auto">{rc.thinking}</pre>
                  </div>
                )}
                {hasTools && (
                  <div className="p-2 bg-dial-black/50 border border-dial-border rounded">
                    <div className="text-[10px] text-indices-muted font-mono uppercase tracking-wider mb-1">Tools</div>
                    <ul className="space-y-1">
                      {rc.tools_used.map((t, i) => (
                        <li key={i} className="text-xs text-indices-medium font-mono">
                          <span className="text-lume-teal">{t.tool}</span>
                          {t.input && <span className="text-indices-muted/70"> · {typeof t.input === 'string' ? t.input : JSON.stringify(t.input)}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function CorrectionModal({ message, onClose, onSubmit, isSubmitting }) {
  const [note, setNote] = useState('')
  const [createDirective, setCreateDirective] = useState(true)
  const [createMemory, setCreateMemory] = useState(true)
  const [sendAsReply, setSendAsReply] = useState(true)

  const preview = message?.content?.slice(0, 200) + (message?.content?.length > 200 ? '...' : '')

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white dark:bg-dial-surface rounded-t-2xl sm:rounded-2xl border dark:border-dial-border shadow-xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b dark:border-dial-border">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-indices-bright">Correct AI Response</h3>
        </div>
        <div className="p-4 space-y-4">
          {/* Original message preview */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-indices-muted uppercase tracking-wide">Original AI Response</label>
            <p className="mt-1 text-sm text-gray-600 dark:text-indices-medium bg-gray-50 dark:bg-dial-elevated p-3 rounded-lg line-clamp-4">
              {preview}
            </p>
          </div>

          {/* Correction note */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-indices-muted uppercase tracking-wide">
              What was wrong and what's correct?
            </label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
              className="mt-1 w-full px-3 py-2 bg-white dark:bg-dial-elevated dark:text-indices-bright border border-gray-300 dark:border-dial-border rounded-lg focus:ring-2 focus:ring-lume-teal focus:border-lume-teal outline-none text-sm resize-none"
              placeholder="e.g., The AI recommended exercises that aren't safe for this client's knee injury..."
              autoFocus
            />
          </div>

          {/* Options */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={createDirective} onChange={e => setCreateDirective(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-dial-border text-lume-teal focus:ring-lume-teal" />
              <span className="text-sm text-gray-700 dark:text-indices-medium">Create AI directive (prevents repeat mistakes)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={createMemory} onChange={e => setCreateMemory(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-dial-border text-lume-teal focus:ring-lume-teal" />
              <span className="text-sm text-gray-700 dark:text-indices-medium">Save as critical memory</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={sendAsReply} onChange={e => setSendAsReply(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 dark:border-dial-border text-lume-teal focus:ring-lume-teal" />
              <span className="text-sm text-gray-700 dark:text-indices-medium">Send correction to client</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t dark:border-dial-border flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-indices-medium bg-gray-100 dark:bg-dial-elevated rounded-lg hover:bg-gray-200 dark:hover:bg-dial-border transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit({ correctionNote: note, createDirective, createMemory, sendAsReply })}
            disabled={!note.trim() || isSubmitting}
            className="px-4 py-2 text-sm font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Correction'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function ClientChatViewPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const messagesEndRef = useRef(null)
  const textareaRef = useRef(null)
  const queryClient = useQueryClient()
  const [replyText, setReplyText] = useState('')
  const [sending, setSending] = useState(false)
  const [clientOnline, setClientOnline] = useState(false)
  const [correctingMessage, setCorrectingMessage] = useState(null)
  const [isCorrectingSubmit, setIsCorrectingSubmit] = useState(false)

  const { data: conversation, isLoading, error } = useQuery({
    queryKey: ['clientConversation', id],
    queryFn: () => clientConversations.get(id),
  })

  // Check if client is online
  useEffect(() => {
    if (conversation?.client?.id) {
      clientConversations.isClientOnline(conversation.client.id)
        .then(data => setClientOnline(data.online))
        .catch(() => {})

      // Poll every 30s
      const interval = setInterval(() => {
        clientConversations.isClientOnline(conversation.client.id)
          .then(data => setClientOnline(data.online))
          .catch(() => {})
      }, 30000)
      return () => clearInterval(interval)
    }
  }, [conversation?.client?.id])

  // Scroll to bottom when conversation loads
  useEffect(() => {
    if (conversation?.messages?.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
    }
  }, [conversation?.messages?.length])

  async function handleTrainerReply() {
    if (!replyText.trim() || sending) return

    setSending(true)
    try {
      await clientConversations.reply(parseInt(id), replyText.trim())
      setReplyText('')
      // Reset textarea height
      if (textareaRef.current) textareaRef.current.style.height = 'auto'
      // Refresh conversation to show the new message
      queryClient.invalidateQueries({ queryKey: ['clientConversation', id] })
    } catch (err) {
      console.error('Failed to send reply:', err)
      alert('Failed to send reply: ' + (err.message || 'Unknown error'))
    } finally {
      setSending(false)
    }
  }

  if (isLoading) {
    return (
      <div className="h-screen bg-gray-100 dark:bg-dial-black flex flex-col overflow-hidden">
        <PageHeader title="Loading..." showBack backTo="/client-chats" />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-lume-teal" />
        </div>
      </div>
    )
  }

  if (error || !conversation) {
    return (
      <div className="h-screen bg-gray-100 dark:bg-dial-black flex flex-col overflow-hidden">
        <PageHeader title="Error" showBack backTo="/client-chats" />
        <div className="p-4 text-center text-gray-500 dark:text-indices-muted">
          Conversation not found
        </div>
      </div>
    )
  }

  const client = conversation.client
  const messages = conversation.messages || []

  // Handler for submitting a correction
  async function handleSubmitCorrection({ correctionNote, createDirective, createMemory, sendAsReply }) {
    if (!correctingMessage || !correctionNote.trim()) return
    setIsCorrectingSubmit(true)
    try {
      await clientConversations.correctMessage(correctingMessage.id, correctionNote, {
        createDirective,
        createMemory,
        sendAsReply,
      })
      setCorrectingMessage(null)
      queryClient.invalidateQueries({ queryKey: ['clientConversation', id] })
    } catch (err) {
      console.error('Failed to submit correction:', err)
      alert('Failed to submit correction: ' + (err.message || 'Unknown error'))
    } finally {
      setIsCorrectingSubmit(false)
    }
  }

  async function handleFlag(message) {
    try {
      await clientConversations.flagMessage(message.id)
    } catch (e) {
      console.error('Failed to flag message:', e)
    }
  }

  // Handler to create KB article from an AI message
  function handleCreateKB(message) {
    const msgIndex = messages.findIndex(m => m.id === message.id)
    const userMessage = msgIndex > 0 ? messages[msgIndex - 1] : null
    const userQuestion = userMessage?.role === 'user' ? userMessage.content : ''

    const clientName = client ? `${client.firstName} ${client.lastName}` : 'Client'
    const contentTemplate = `## Context
Client asked: "${userQuestion.slice(0, 200)}${userQuestion.length > 200 ? '...' : ''}"

## AI Response (needs correction)
${message.content.slice(0, 500)}${message.content.length > 500 ? '...' : ''}

## Correct Approach
[Describe the correct response or approach here]

## Why This Matters
[Explain why this correction is important]`

    navigate('/knowledge/new', {
      state: {
        prefill: {
          title: '',
          content: contentTemplate,
          category: 'faq',
          tags: 'correction, ai-improvement',
        },
        source: {
          type: 'chat_correction',
          clientName,
          messageId: message.id,
          conversationId: id,
        }
      }
    })
  }

  return (
    <div className="h-screen bg-gray-100 dark:bg-dial-black flex flex-col overflow-hidden">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            {client ? `${client.firstName} ${client.lastName}` : 'Client Chat'}
            {clientOnline && (
              <span className="w-2 h-2 rounded-full bg-green-500 dark:bg-lume-teal inline-block" title="Client online" />
            )}
          </span>
        }
        showBack
        backTo={client ? `/client-chats?clientId=${client.id}` : '/client-chats'}
        actions={
          client && (
            <Link
              to={`/clients/${client.id}`}
              className="p-2 text-primary-600 dark:text-lume-teal hover:text-primary-700 dark:hover:text-lume-green"
              title="View client profile"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </Link>
          )
        }
      />

      {/* Conversation Info Bar */}
      <div className="bg-white dark:bg-dial-surface border-b border-gray-200 dark:border-dial-border px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between text-sm">
          <span className="text-primary-600 dark:text-lume-teal font-medium">
            {conversation.conversationTypeDisplay || conversation.conversationType}
          </span>
          <span className="text-gray-500 dark:text-indices-muted">
            {conversation.messageCount} messages
          </span>
        </div>
        {client?.goals && (
          <p className="text-xs text-gray-400 dark:text-indices-muted mt-1 truncate">
            Goals: {client.goals}
          </p>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-indices-muted mt-8">
            No messages in this conversation
          </div>
        ) : (
          <>
            {conversation.messagesLimited && (
              <div className="text-center text-xs text-gray-400 dark:text-indices-muted mb-4 py-2 bg-gray-100 dark:bg-dial-elevated rounded">
                Showing last {messages.length} of {conversation.messageCount} messages
              </div>
            )}
            {messages.map((message, index) => {
              const prevMessage = messages[index - 1]
              const showDate = !prevMessage || formatDate(message.createdAt) !== formatDate(prevMessage.createdAt)

              return (
                <div key={message.id}>
                  {showDate && (
                    <div className="text-center text-xs text-gray-500 dark:text-indices-muted my-4">
                      {formatDate(message.createdAt)}
                    </div>
                  )}
                  <Message
                    message={message}
                    isUser={message.role === 'user'}
                    isTrainer={message.role === 'trainer'}
                    onCreateKB={handleCreateKB}
                    onCorrect={message.role === 'assistant' ? setCorrectingMessage : undefined}
                    onFlag={message.role === 'assistant' ? handleFlag : undefined}
                  />
                </div>
              )
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Reply Input */}
      <div className="bg-white dark:bg-dial-surface border-t border-gray-200 dark:border-dial-border px-4 py-3 flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={replyText}
            onChange={(e) => {
              setReplyText(e.target.value)
              e.target.style.height = 'auto'
              e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px'
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleTrainerReply()
              }
            }}
            rows={1}
            placeholder="Reply as trainer..."
            disabled={sending}
            className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-dial-elevated dark:text-indices-bright rounded-2xl border-0 focus:ring-2 focus:ring-primary-500 dark:focus:ring-lume-teal outline-none text-sm disabled:opacity-50 resize-none max-h-40 overflow-y-auto"
          />
          <button
            onClick={handleTrainerReply}
            onMouseDown={(e) => e.preventDefault()}
            disabled={!replyText.trim() || sending}
            className="px-5 py-2.5 bg-primary-600 dark:bg-lume-teal text-white dark:text-dial-black rounded-full text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700 dark:hover:bg-lume-green transition-colors"
          >
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
        <p className="text-xs text-gray-400 dark:text-indices-muted mt-1.5 px-1">
          Your message will appear in the client's AI chat — the AI will incorporate it naturally
        </p>
      </div>

      {/* Correction Modal */}
      {correctingMessage && (
        <CorrectionModal
          message={correctingMessage}
          onClose={() => setCorrectingMessage(null)}
          onSubmit={handleSubmitCorrection}
          isSubmitting={isCorrectingSubmit}
        />
      )}
    </div>
  )
}
